import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, forwardRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { StorageType } from 'mbs-ui-shared@models';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { ProxmoxModelForStepsHelper } from 'wizards/helpers/proxmox-model-helpers/proxmox-model-for-steps-helper';
import { ProxmoxPlanFromStepsHelper } from 'wizards/helpers/proxmox-model-helpers/proxmox-plan-from-steps-helper';
import { VmwarePlanFromStepsHelper } from 'wizards/helpers/vmware-model-helpers/vmware-plan-from-steps-helper';
import { ProxmoxFormModel } from 'wizards/models/proxmox/proxmox-form-model';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { AdvancedSettingsVirtualStepComponent } from 'wizards/steps/advanced-settings-virtual-step/advanced-settings-virtual-step.component';
import { ConsistencyStepComponent } from 'wizards/steps/archive-consistency-step/consistency-step.component';
import { DestinationPlusRetentionStepComponent } from 'wizards/steps/combined/destination-plus-retention-step/destination-plus-retention-step.component';
import { CompressionEncryptionStepComponent } from 'wizards/steps/compression-encryption-step/compression-encryption-step.component';
import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';
import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';
import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';
import { NBFRetentionPolicyStepComponent } from 'wizards/steps/retention-policy-step/NBF/retention-policy-step.component';
import { RetentionPolicyStepComponent } from 'wizards/steps/retention-policy-step/retention-policy-step.component';
import { NBFScheduleStepComponent } from 'wizards/steps/schedule-step/NBF/schedule-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { SelectHostStepComponent } from 'wizards/steps/select-host-step/select-host-step.component';
import { SelectVirtualMachinesStepComponent } from 'wizards/steps/select-virtual-machines-step/select-virtual-machines-step.component';
import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';
import { VMWizardBaseClass } from '../vm-wizard-base.class';

type ProxmoxBackupWizardHelpDocumentationLinks =
  | 'welcome'
  | 'whereToBackUp'
  | 'destinationPlusRetention'
  | 'connectHost'
  | 'whatToBackUp'
  | 'advancedSettings'
  | 'compressionEncryption'
  | 'backupConsistencyCheck'
  | 'schedule'
  | 'retention'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-proxmox-backup-wizard',
  templateUrl: './proxmox-backup-wizard.component.html',
  providers: [
    {
      provide: VMWizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => ProxmoxBackupWizardComponent)
    }
  ],
  imports: [
    AdvancedSettingsVirtualStepComponent,
    AsyncPipe,
    CommonModule,
    CompressionEncryptionStepComponent,
    ConsistencyStepComponent,
    FormsModule,
    NBFRetentionPolicyStepComponent,
    NBFScheduleStepComponent,
    NotificationsStepComponent,
    PlanNameStepComponent,
    PrePostActionsStepComponent,
    RetentionPolicyStepComponent,
    ScheduleStepComponent,
    SelectHostStepComponent,
    SelectVirtualMachinesStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe,
    DestinationPlusRetentionStepComponent
  ]
})
export class ProxmoxBackupWizardComponent extends VMWizardBaseClass {
  i18nPipe: I18NextPipe;

  public readonly storageType = StorageType;

  public FormModel: ProxmoxFormModel;
  public hostTitle = this.i18nPipe.transform('wizards:select_host_step_title');
  public encryptionTitle = this.i18nPipe.transform('wizards:encryption_options_step_title');
  public helpLink: WizardHelpLink<ProxmoxBackupWizardHelpDocumentationLinks>;

  public canSyntheticForProxmox: boolean;

  constructor() {
    const i18nPipe = inject(I18NextPipe);

    super();
    this.i18nPipe = i18nPipe;
    this.canSyntheticForProxmox = this.mainService?.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, 3) >= 865;

    this.FormModel = new ProxmoxFormModel(this.isUnix, this.isNBF);

    this.initSubscribes();
  }

  protected generateFormModel(plan) {
    this.FormModel = ProxmoxModelForStepsHelper.ProxmoxModelFromPlan(plan, this.defaultSettings());
    this.FormModel.compressionEncryptionStep.UseCompression = false;

    if (!this.canSyntheticForProxmox) this.FormModel.advancedSettingsStep.syntheticFull = false;

    if (this.isEdit) setTimeout(() => this.storageManager.changeVMStorageHandler(this.getSelectedStorage));
  }

  changeProxmoxStorageHandler(connection: StorageConnection): void {
    this.storageManager.changeVMStorageHandler(connection);
  }

  protected loadInfoHandlerFinally() {
    this.cdr.detectChanges();
  }

  protected clearAppAwareData(newPlan) {
    VmwarePlanFromStepsHelper.clearAppAwareData(newPlan);
  }

  protected getPlanForSaving(): any {
    return ProxmoxPlanFromStepsHelper.getUpdatedPlan(this.loadedPlan.plan, this.FormModel, this.defaultSettings());
  }

  getRAWHelpLinks(): Record<ProxmoxBackupWizardHelpDocumentationLinks, string> {
    return {
      welcome: 'backup/proxmox/proxmox#planname',
      whereToBackUp: 'backup/proxmox/proxmox#storage',
      destinationPlusRetention: 'backup/proxmox/prox-console#where',
      connectHost: 'backup/proxmox/proxmox#server',
      whatToBackUp: 'backup/proxmox/proxmox#vm',
      advancedSettings: 'backup/proxmox/vmware#advanced',
      compressionEncryption: 'backup/proxmox/proxmox#compression',
      backupConsistencyCheck: 'backup/proxmox/proxmox#check',
      schedule: 'backup/proxmox/proxmox#schedule',
      retention: 'backup/proxmox/proxmox#retention',
      prePost: 'backup/proxmox/proxmox#prepost',
      notification: 'backup/proxmox/proxmox#log'
    };
  }
}
